import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useLiveData } from "@/context/LiveDataContext";
import MapView from "@/components/MapView";
import DroneDetailPanel from "@/components/DroneDetailPanel";
import ReplayPanel from "@/components/ReplayPanel";
import FloatingCameraWindow from "@/components/FloatingCameraWindow";
import { Dot } from "@/components/common";
import { statusColor, num, fmtTime, batteryShort } from "@/lib/format";
import { Crosshair, Route, Navigation as NavIcon, MapPin, Flag, Home, History, Play, Pause, SkipBack, SkipForward, Video, LocateFixed } from "lucide-react";
import { buildReplayNav } from "@/lib/mapNavigation";

function haversineKm(a, b) {
  const r = 6371, t = (x) => (x * Math.PI) / 180;
  const h = Math.sin(t(b[0] - a[0]) / 2) ** 2 + Math.cos(t(a[0])) * Math.cos(t(b[0])) * Math.sin(t(b[1] - a[1]) / 2) ** 2;
  return r * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

export default function LiveMap() {
  const { drones, fleetConnected, refresh } = useLiveData();
  const [params, setParams] = useSearchParams();
  const replayMissionId = params.get("mission");
  const [selected, setSelected] = useState(params.get("id") || null);
  const [recenter, setRecenter] = useState(null);
  const [mission, setMission] = useState(null);
  const [realPath, setRealPath] = useState([]);
  const [config, setConfig] = useState(null);
  const [showPlanned, setShowPlanned] = useState(true);
  const [showReal, setShowReal] = useState(true);
  const [replayPoints, setReplayPoints] = useState([]);
  const [rIdx, setRIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  // MAP.NAV.1 — SUIVRE le drone sélectionné (désactivé à chaque changement de sélection, nord en haut).
  const [follow, setFollow] = useState(false);
  useEffect(() => { setFollow(false); }, [selected]);
  const replay = !!replayMissionId;

  useEffect(() => {
    if (!selected && drones.length) setSelected(drones[0].drone_id);
  }, [drones, selected]);

  const sel = drones.find((d) => d.drone_id === selected);
  const selectedRef = useRef(selected);
  selectedRef.current = selected;

  // Replay mode: load a specific (possibly completed) mission frozen + full telemetry points
  useEffect(() => {
    if (!replay) return;
    setPlaying(false);
    setRIdx(0);
    (async () => {
      try {
        const { data } = await api.get(`/missions/${replayMissionId}`);
        const m = data.mission;
        setMission(m);
        setSelected(m.drone_id);
        // Full telemetry points for this mission (battery/alt/speed/signal/mode + lat/lng + timestamp)
        const { data: th } = await api.get(`/drones/${m.drone_id}/telemetry-history`, {
          params: { mission_id: m.id, max_points: 2000 },
        });
        let pts = (th.points || []).filter((p) => p.lat != null && p.lng != null);
        if (pts.length === 0) {
          // Fallback to /track if no full telemetry points stored for this mission
          const { data: t } = await api.get(`/drones/${m.drone_id}/track`, { params: { mission_id: m.id, max_points: 2000 } });
          pts = (t.points || []).filter((p) => p.lat != null && p.lng != null);
        }
        setReplayPoints(pts);
        setRealPath(pts.map((p) => [p.lat, p.lng]));
        setRIdx(pts.length ? pts.length - 1 : 0);
        if (m.lat) setRecenter([m.lat, m.lng]);
      } catch (e) { /* ignore */ }
    })();
  }, [replay, replayMissionId]);

  // Auto-play (x1): advance ~1 point/sec
  useEffect(() => {
    if (!replay || !playing || replayPoints.length === 0) return;
    const iv = setInterval(() => {
      setRIdx((i) => {
        if (i >= replayPoints.length - 1) { setPlaying(false); return i; }
        return i + 1;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [replay, playing, replayPoints.length]);

  const onRealPathClick = useCallback((latlng) => {
    if (!replay || replayPoints.length === 0) return;
    let best = 0, bestD = Infinity;
    replayPoints.forEach((p, i) => {
      const d = (p.lat - latlng.lat) ** 2 + (p.lng - latlng.lng) ** 2;
      if (d < bestD) { bestD = d; best = i; }
    });
    setRIdx(best);
    setPlaying(false);
  }, [replay, replayPoints]);

  const loadTrajectory = useCallback(async (id) => {
    if (!id || replay) return;
    try {
      const { data: m } = await api.get(`/drones/${id}/mission/active`);
      // Mission active -> tracé scopé à la mission ; sinon -> session de vol COURANTE
      // (réinitialisée à chaque redémarrage du drone, ne persiste plus indéfiniment).
      const qp = m.mission?.id ? { mission_id: m.mission.id } : { session: "current" };
      const { data: t } = await api.get(`/drones/${id}/track`, { params: { ...qp, max_points: 1000 } });
      if (selectedRef.current !== id) return;
      setMission(m.mission);
      setRealPath((t.points || []).filter((p) => p.lat && p.lng).map((p) => [p.lat, p.lng]));
    } catch (e) {
      if (selectedRef.current === id) { setMission(null); setRealPath([]); }
    }
  }, [replay]);

  const loadConfig = useCallback(async (id) => {
    if (!id) return;
    try {
      const { data } = await api.get(`/drones/${id}/config`);
      if (selectedRef.current === id) setConfig(data.config);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { loadTrajectory(selected); loadConfig(selected); }, [selected, loadTrajectory, loadConfig]);

  // Reset IMMÉDIAT du tracé réel quand le drone redémarre (nouvelle session de vol).
  const prevSession = useRef(null);
  useEffect(() => {
    if (replay) return;
    const sid = sel?.flight_session_id;
    if (sid && prevSession.current && sid !== prevSession.current) {
      setRealPath([]);
    }
    if (sid) prevSession.current = sid;
  }, [sel?.flight_session_id, replay]);

  useEffect(() => {
    if (!selected || replay) return;
    const iv = setInterval(() => loadTrajectory(selected), 15000);
    return () => clearInterval(iv);
  }, [selected, loadTrajectory, replay]);

  const focus = (id, opts) => {
    if (replay) { setParams({ id }); return; }
    setSelected(id);
    if (opts && opts.source === "map") return; // MAP.NAV.3 : un clic marqueur ne recentre pas la carte
    const d = drones.find((x) => x.drone_id === id);
    if (d?.lat) setRecenter([d.lat, d.lng]);
  };

  const startPoint = mission && mission.start_lat != null ? { lat: mission.start_lat, lng: mission.start_lng } : null;
  const dronePos0 = sel?.lat != null ? [sel.lat, sel.lng] : null;

  // Trajectoire PRÉVUE : visible dès l'envoi de la mission, masquée waypoint par waypoint
  // dès que le drone atteint le point (< 10 m). En relecture, on garde le tracé complet.
  const ARRIVE_M = 10;
  const mWaypoints = mission?.waypoints || [];
  let plannedPath = mission?.planned_path || null;
  let destination = mission && mission.lat != null ? { lat: mission.lat, lng: mission.lng } : null;
  let waypoints = mWaypoints;
  if (!replay && mission && dronePos0) {
    if (mWaypoints.length > 0) {
      const nextIdx = mWaypoints.findIndex((w) => haversineKm(dronePos0, [w.lat, w.lng]) * 1000 > ARRIVE_M);
      if (nextIdx === -1) {
        plannedPath = null; destination = null; waypoints = [];   // tous les points atteints
      } else {
        const remaining = mWaypoints.slice(nextIdx);
        waypoints = remaining;
        plannedPath = [dronePos0, ...remaining.map((w) => [w.lat, w.lng])];
        const last = mWaypoints[mWaypoints.length - 1];
        destination = { lat: last.lat, lng: last.lng };
      }
    } else if (destination && haversineKm(dronePos0, [destination.lat, destination.lng]) * 1000 <= ARRIVE_M) {
      plannedPath = null; destination = null;
    }
  }

  // Base point
  let basePoint = null;
  if (config) {
    if (config.base_mode === "CUSTOM_BASE" && config.base_lat != null)
      basePoint = { lat: config.base_lat, lng: config.base_lng, label: config.base_address };
    else if (config.base_mode === "ARDUPILOT_HOME" && config.ardupilot_home_lat != null)
      basePoint = { lat: config.ardupilot_home_lat, lng: config.ardupilot_home_lng, label: "Home ArduPilot" };
  }
  const dronePos = sel?.lat != null ? [sel.lat, sel.lng] : null;
  const baseLink = basePoint && dronePos ? [dronePos, [basePoint.lat, basePoint.lng]] : null;
  const baseDist = baseLink ? haversineKm(baseLink[0], baseLink[1]) : null;
  const baseEta = baseDist != null && sel?.speed > 0 ? (baseDist * 1000) / sel.speed : null;

  return (
    <div className="h-full flex">
      <div className="flex-1 relative">
        {replay && (
          <div data-testid="replay-banner" className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-[#2DD4BF] border border-[#2DD4BF]/40 bg-[#0A0A0A]/90 rounded-sm px-3 py-1.5">
            <History size={12} /> Relecture mission {mission?.status === "completed" ? "(terminée)" : ""}
            <button onClick={() => setParams({ id: selected || "" })} className="ml-2 text-[#A3A3A3] hover:text-white">✕ live</button>
          </div>
        )}
        {!fleetConnected && !replay && (
          <div data-testid="live-interrupted" className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] font-mono text-[10px] uppercase tracking-wider text-[#F59E0B] border border-[#F59E0B]/40 bg-[#0A0A0A]/90 rounded-sm px-3 py-1.5">
            ● Connexion live interrompue
          </div>
        )}

        {!replay && sel && (
          <button data-testid="map-follow-toggle" onClick={() => setFollow((f) => !f)} title="Suivre le drone sélectionné (cap vers le haut)"
            className={`absolute top-3 right-40 z-[1000] flex items-center gap-1.5 bg-[#0A0A0A]/90 border rounded-sm px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider ${follow ? "border-[#22D3EE] text-[#22D3EE] bg-[#22D3EE]/10" : "border-white/20 text-white/80 hover:bg-white/5"}`}>
            <LocateFixed size={13} /> {follow ? `Suivi ${sel.drone_id}` : "Suivre"}
          </button>
        )}
        {!replay && sel && (
          <button data-testid="map-open-camera" onClick={() => setShowCamera(true)}
            className="absolute top-3 right-3 z-[1000] flex items-center gap-1.5 bg-[#0A0A0A]/90 border border-[#3B82F6]/50 text-[#3B82F6] rounded-sm px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider hover:bg-[#3B82F6]/10">
            <Video size={13} /> Ouvrir caméra
          </button>
        )}

        <div className="absolute top-3 left-3 z-[1000] flex flex-col gap-2">
          {drones.map((d) => (
            <button key={d.drone_id} data-testid={`map-chip-${d.drone_id}`} onClick={() => focus(d.drone_id)}
              className={`flex items-center gap-2 bg-[#0A0A0A]/90 border rounded-sm px-3 py-1.5 transition-colors ${selected === d.drone_id ? "border-[#3B82F6]" : "border-white/15 hover:border-white/30"}`}>
              <Dot color={statusColor(d)} pulse={d.status === "online"} />
              <span className="font-mono text-xs">{d.drone_id}</span>
              <span className="font-mono text-[10px] text-[#A3A3A3]">{batteryShort(d)}</span>
              <Crosshair size={12} className="text-[#525252]" />
            </button>
          ))}
        </div>

        <div data-testid="map-legend" className="absolute bottom-4 left-3 z-[1000] bg-[#0A0A0A]/92 border border-white/15 rounded-sm p-3 space-y-2 w-60">
          <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#525252]">Trajectoires & Base</div>
          <LegendRow icon={<NavIcon size={12} className="text-[#22C55E]" />} label="Position actuelle" />
          <LegendRow icon={<Flag size={12} className="text-[#22C55E]" />} label="Point de départ" />
          <LegendRow icon={<MapPin size={12} className="text-[#3B82F6]" />} label="Destination" />
          <LegendRow icon={<Home size={12} className="text-[#F59E0B]" />} label={basePoint ? "Base / Home" : "Base non configurée"} />
          <button data-testid="toggle-planned" onClick={() => setShowPlanned((v) => !v)}
            className={`w-full flex items-center gap-2 font-mono text-[11px] rounded-sm px-2 py-1 transition-colors ${showPlanned ? "text-white" : "text-[#525252]"} hover:bg-white/5`}>
            <span className="inline-block w-5 border-t-2 border-dashed border-[#3B82F6]" /> Trajectoire prévue
            <span className="ml-auto text-[9px] uppercase">{showPlanned ? "ON" : "OFF"}</span>
          </button>
          <button data-testid="toggle-real" onClick={() => setShowReal((v) => !v)}
            className={`w-full flex items-center gap-2 font-mono text-[11px] rounded-sm px-2 py-1 transition-colors ${showReal ? "text-white" : "text-[#525252]"} hover:bg-white/5`}>
            <span className="inline-block w-5 border-t-2 border-[#2DD4BF]" /> Trajectoire réelle
            <span className="ml-auto text-[9px] uppercase">{showReal ? "ON" : "OFF"}</span>
          </button>
          {baseDist != null && (
            <div data-testid="base-distance" className="font-mono text-[10px] text-[#F59E0B] flex items-center gap-1 border-t border-white/10 pt-1.5">
              <Home size={10} /> {num(baseDist, 2)} km
              {baseEta != null && <span className="text-[#A3A3A3]">· retour ~{Math.round(baseEta)}s</span>}
            </div>
          )}
          <div className="font-mono text-[9px] text-[#525252] flex items-center gap-1">
            <Route size={10} /> {realPath.length} pts réels {mission ? `· ${mission.mission_type}` : "· aucune mission active"}
          </div>
        </div>

        {/* Replay scrubber */}
        {replay && replayPoints.length > 0 && (
          <div data-testid="replay-scrubber" className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[1000] bg-[#0A0A0A]/95 border border-[#2DD4BF]/40 rounded-sm p-3 w-[520px] max-w-[80vw]">
            <div className="flex items-center justify-between mb-2 font-mono text-[10px] text-[#A3A3A3]">
              <span>Point {rIdx + 1}/{replayPoints.length}</span>
              <span className="text-[#2DD4BF]">{fmtTime(replayPoints[rIdx]?.timestamp)}</span>
            </div>
            <input data-testid="replay-slider" type="range" min={0} max={replayPoints.length - 1} value={rIdx}
              onChange={(e) => { setRIdx(Number(e.target.value)); setPlaying(false); }}
              className="w-full accent-[#2DD4BF]" />
            <div className="flex items-center justify-center gap-2 mt-2">
              <button data-testid="replay-prev" onClick={() => { setRIdx((i) => Math.max(0, i - 1)); setPlaying(false); }}
                className="w-8 h-8 flex items-center justify-center border border-white/15 rounded-sm hover:bg-white/10"><SkipBack size={14} /></button>
              <button data-testid="replay-playpause" onClick={() => setPlaying((p) => !p)}
                className="w-10 h-8 flex items-center justify-center bg-[#2DD4BF] text-[#0A0A0A] rounded-sm hover:bg-[#5eead4]">
                {playing ? <Pause size={15} /> : <Play size={15} />}
              </button>
              <button data-testid="replay-next" onClick={() => { setRIdx((i) => Math.min(replayPoints.length - 1, i + 1)); setPlaying(false); }}
                className="w-8 h-8 flex items-center justify-center border border-white/15 rounded-sm hover:bg-white/10"><SkipForward size={14} /></button>
              <span className="font-mono text-[9px] uppercase tracking-wider text-[#525252] ml-2">{playing ? "Lecture x1" : "Pause"}</span>
            </div>
          </div>
        )}

        <MapView drones={replay ? [] : drones} selectedId={selected} onSelect={focus} recenter={recenter} showHealth
          startPoint={startPoint} destination={destination} plannedPath={plannedPath}
          realPath={realPath} waypoints={waypoints} showPlanned={showPlanned} showReal={showReal}
          basePoint={basePoint} baseLink={baseLink}
          cursorPoint={replay && replayPoints[rIdx] ? replayPoints[rIdx] : null}
          cursorNav={replay && replayPoints[rIdx] ? buildReplayNav(replayPoints, rIdx) : null}
          followId={!replay && follow && sel ? sel.drone_id : null} onFollowChange={setFollow}
          onRealPathClick={replay ? onRealPathClick : undefined} />
      </div>

      {replay ? (
        mission && (
          <div className="w-[340px] shrink-0">
            <ReplayPanel mission={mission} points={replayPoints} idx={rIdx} />
          </div>
        )
      ) : (
        sel && (
          <div className="w-[340px] shrink-0">
            <DroneDetailPanel droneId={sel.drone_id} base={sel} onRefresh={() => { refresh(); loadTrajectory(sel.drone_id); loadConfig(sel.drone_id); }} />
          </div>
        )
      )}

      {showCamera && sel && <FloatingCameraWindow droneId={sel.drone_id} onClose={() => setShowCamera(false)} />}
    </div>
  );
}

function LegendRow({ icon, label }) {
  return <div className="flex items-center gap-2 font-mono text-[11px] text-[#A3A3A3]">{icon} {label}</div>;
}
